import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = (process.env.REACT_APP_API_BASE || '/api').replace(/["']/g, '').replace(/\/+$/, '');

export default function AdminAssignStudentToParent() {
  const navigate = useNavigate();
  const [parents, setParents] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [availableStudents, setAvailableStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    loadParents();
  }, []);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      fetchAvailableStudents(searchQuery);
    } else {
      setAvailableStudents([]);
    }
  }, [searchQuery]);

  const loadParents = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/admin/parents`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error('Failed to load parents');
      const data = await res.json();
      setParents(data);
      if (data.length > 0 && !selectedParent) setSelectedParent(data[0]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableStudents = async (query) => {
    try {
      const res = await fetch(`${API_BASE}/admin/students/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error('Failed to search students');
      const data = await res.json();
      setAvailableStudents(data);
    } catch (err) {
      console.error('Search error:', err);
    }
  };

  const handleAssign = async () => {
    if (!selectedParent || !selectedStudentId) return;
    setError('');
    setSuccessMsg('');
    
    try {
      const res = await fetch(`${API_BASE}/admin/parents/${selectedParent.id}/assign-student`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        },
        body: JSON.stringify({ student_id: parseInt(selectedStudentId) })
      });
      
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to assign');
      
      setSuccessMsg(result.message);
      setSelectedStudentId('');
      setSearchQuery('');
      setAvailableStudents([]);
      loadParents(); // Refresh to show new assignment
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemove = async (studentId) => {
    if (!window.confirm('Are you sure you want to unlink this student from the parent?')) return;
    try {
      const res = await fetch(`${API_BASE}/admin/parents/${selectedParent.id}/students/${studentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error('Failed to remove');
      setSuccessMsg('Student unlinked successfully.');
      loadParents();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>⏳ Loading...</div>;

  return (
    <div style={{ maxWidth: 1000, margin: '40px auto', padding: 24, background: '#f8f9fa', borderRadius: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>👨‍👩‍👧‍👦 Assign Students to Parents</h2>
        <button onClick={() => navigate('/admin/dashboard')} style={{ padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>← Back to Dashboard</button>
      </div>

      {error && <div style={{ padding: 12, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 15 }}>❌ {error}</div>}
      {successMsg && <div style={{ padding: 12, background: '#d4edda', color: '#155724', borderRadius: 6, marginBottom: 15 }}>✅ {successMsg}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        
        {/* LEFT: Parent List & Current Assignments */}
        <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
          <h3 style={{ marginTop: 0, marginBottom: 15 }}>1. Select a Parent</h3>
          <select 
            value={selectedParent?.id || ''} 
            onChange={(e) => {
              const parent = parents.find(p => p.id === parseInt(e.target.value));
              setSelectedParent(parent);
              setError('');
              setSuccessMsg('');
            }}
            style={{ width: '100%', padding: 10, marginBottom: 20, borderRadius: 6, border: '1px solid #ccc' }}
          >
            {parents.map(p => (
              <option key={p.id} value={p.id}>{p.name} ({p.email})</option>
            ))}
          </select>

          {selectedParent && (
            <div>
              <h4 style={{ fontSize: 14, color: '#666', marginBottom: 10 }}>Currently Assigned Students ({selectedParent.students.length}/5)</h4>
              {selectedParent.students.length === 0 ? (
                <p style={{ color: '#999', fontStyle: 'italic', fontSize: 14 }}>No students assigned yet.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {selectedParent.students.map((student, idx) => (
                    <li key={student.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: '#f8f9fa', borderRadius: 6, marginBottom: 8, border: '1px solid #e9ecef' }}>
                      <span>
                        <strong>{idx + 1}.</strong> {student.name}
                      </span>
                      <button 
                        onClick={() => handleRemove(student.id)}
                        style={{ padding: '4px 10px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: Assign New Student */}
        <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
          <h3 style={{ marginTop: 0, marginBottom: 15 }}>2. Assign New Student</h3>
          
          {selectedParent && selectedParent.students.length >= 5 ? (
            <div style={{ padding: 15, background: '#fff3cd', color: '#856404', borderRadius: 6, fontSize: 14 }}>
              ⚠️ This parent has reached the maximum limit of 5 students. Please remove a student before assigning a new one.
            </div>
          ) : (
            <>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 14 }}>Search Student (Name or ID)</label>
              <input 
                type="text" 
                placeholder="Type at least 2 characters..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '100%', padding: 10, marginBottom: 15, borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' }}
              />

              {availableStudents.length > 0 && (
                <div style={{ marginBottom: 15 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 14 }}>Select Student</label>
                  <select 
                    value={selectedStudentId} 
                    onChange={(e) => setSelectedStudentId(e.target.value)}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' }}
                  >
                    <option value="">-- Choose a student --</option>
                    {availableStudents.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.first_name} {s.last_name} (ID: {s.student_id})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <button 
                onClick={handleAssign}
                disabled={!selectedStudentId || (selectedParent && selectedParent.students.length >= 5)}
                style={{ 
                  width: '100%', 
                  padding: 12, 
                  background: (!selectedStudentId || (selectedParent && selectedParent.students.length >= 5)) ? '#ccc' : '#28a745', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: 6, 
                  cursor: (!selectedStudentId || (selectedParent && selectedParent.students.length >= 5)) ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: 15
                }}
              >
                ✅ Assign Student
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}